/*
 * Throwaway preview — shows the three open questions as toggleable options:
 *   A. Popup style (drawer · modal · new page)
 *   B. Storefront-mode "View profile" affordance next to the seller name
 *   C. Profile-page close behaviour
 *
 * Hit /preview/seller-profile-popup-options. Pure mockup — no real data fetched.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  X, Eye, ExternalLink, Info, ChevronDown, ArrowLeft, Maximize2,
  Store, ShieldCheck, MapPin, CheckCircle2, Sparkles, Check,
} from "lucide-react";

const SECTIONS = [
  { key: "popup",      label: "1 · Popup style"       },
  { key: "topbar",     label: "2 · Storefront top-bar link" },
  { key: "close",      label: "3 · Close / back behaviour" },
];

export default function SellerProfilePopupOptionsPreview() {
  const [section, setSection] = useState("popup");
  return (
    <div className="min-h-screen bg-secondary/30">
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-[1240px] mx-auto px-8 py-4 flex items-center gap-6 flex-wrap">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/50">Preview · pick one of each</div>
            <div className="display text-lg mt-0.5">Profile — popup · top-bar · close</div>
          </div>
          <div className="ml-auto flex items-center gap-1 rounded-full border border-border bg-white p-1">
            {SECTIONS.map(s => (
              <button key={s.key}
                onClick={() => setSection(s.key)}
                className={`rounded-full px-4 h-9 text-[12px] mono uppercase tracking-[0.12em] transition-colors ${section === s.key ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}>
                {s.label}
              </button>
            ))}
          </div>
          <Link to="/" className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/50 hover:text-foreground">← Back</Link>
        </div>
      </div>

      <div className="max-w-[1240px] mx-auto px-8 py-10">
        {section === "popup"  && <PopupOptions/>}
        {section === "topbar" && <TopBarOptions/>}
        {section === "close"  && <CloseOptions/>}
      </div>
    </div>
  );
}

/* ═══ SECTION 1 — POPUP STYLE ═════════════════════════════════════════ */
function PopupOptions() {
  const [pick, setPick] = useState("drawer");
  return (
    <div>
      <Header
        eyebrow="Question 1"
        title="How should the profile open when a buyer clicks?"
        sub="Today it loads as a full page. Three alternatives below — click a tab to preview each."
      />

      <PickerTabs value={pick} onChange={setPick}
        items={[
          { k: "drawer", label: "A · Side drawer", rec: "Recommended" },
          { k: "modal",  label: "B · Centered modal" },
          { k: "page",   label: "C · Full page (current)" },
        ]}/>

      <div className="mt-8">
        {pick === "drawer" && <PreviewDrawer/>}
        {pick === "modal"  && <PreviewModal/>}
        {pick === "page"   && <PreviewFullPage/>}
      </div>

      <ProsCons
        title={pick === "drawer" ? "Side drawer · pros & cons"
             : pick === "modal"  ? "Centered modal · pros & cons"
             :                     "Full page · pros & cons"}
        rows={
          pick === "drawer" ? [
            ["Pro", "Buyer never leaves their place — order, cart, page state all preserved."],
            ["Pro", "Backdrop click + Esc key + X button → three intuitive ways to close."],
            ["Pro", "Mobile-friendly: drawer slides up on phones, full-screen feel without an extra tab."],
            ["Pro", "Fast — no page navigation, no spinner. Profile fetches once and caches."],
            ["Con", "Less shareable than a real URL — but we still expose the full /s/:handle URL inside the drawer (small \"Open full page\" link), so sellers can copy/share."],
          ] : pick === "modal" ? [
            ["Pro", "Familiar pattern. Visually centred."],
            ["Con", "Fixed-size containers fight tall content (about + policies + trust = ~1100px)."],
            ["Con", "Less mobile-friendly — modals on phones feel clipped."],
            ["Con", "Can't keep the buyer's previous view glanceable underneath."],
          ] : [
            ["Pro", "Most shareable: the URL bar matches what the buyer is on."],
            ["Pro", "No layering tricks — straight, simple page."],
            ["Con", "Buyer loses their context — back-button is the only escape (current bug you noted)."],
            ["Con", "Click-cost is high — each profile view = a navigation."],
          ]
        }/>
    </div>
  );
}

function PreviewDrawer() {
  return (
    <FrameMockup>
      {/* Mock buyer page underneath */}
      <BuyerPageBackdrop dimmed/>

      {/* Drawer */}
      <div className="absolute inset-y-0 right-0 w-[640px] max-w-[80%] bg-white border-l border-border shadow-2xl overflow-hidden flex flex-col">
        <div className="px-5 py-3.5 border-b border-border flex items-center gap-2.5">
          <div className="w-7 h-7 rounded grid place-items-center font-bold display bg-[#0a1f1c] text-[#e8ff3a] text-sm">G</div>
          <div className="font-medium text-[14px] flex-1 truncate">GlowCo Beauty · Profile</div>
          <a href="#" className="text-[11.5px] text-foreground/55 hover:text-foreground inline-flex items-center gap-1">
            <Maximize2 className="w-3 h-3"/> Open full
          </a>
          <button className="w-7 h-7 rounded grid place-items-center hover:bg-secondary text-foreground/65"><X className="w-4 h-4"/></button>
        </div>
        <div className="flex-1 overflow-auto p-5">
          <DrawerProfileContent/>
        </div>
      </div>

      {/* Caption */}
      <CaptionBubble>
        Click backdrop · press Esc · click X · or "Open full" → /s/:handle in a new tab
      </CaptionBubble>
    </FrameMockup>
  );
}

function PreviewModal() {
  return (
    <FrameMockup>
      <BuyerPageBackdrop dimmed/>
      <div className="absolute inset-0 grid place-items-center p-12">
        <div className="w-[640px] max-w-full max-h-[78%] bg-white rounded-2xl border border-border shadow-2xl overflow-hidden flex flex-col">
          <div className="px-5 py-3.5 border-b border-border flex items-center gap-2.5">
            <div className="w-7 h-7 rounded grid place-items-center font-bold display bg-[#0a1f1c] text-[#e8ff3a] text-sm">G</div>
            <div className="font-medium text-[14px] flex-1 truncate">GlowCo Beauty · Profile</div>
            <button className="w-7 h-7 rounded grid place-items-center hover:bg-secondary text-foreground/65"><X className="w-4 h-4"/></button>
          </div>
          <div className="flex-1 overflow-auto p-5">
            <DrawerProfileContent/>
          </div>
        </div>
      </div>
      <CaptionBubble>Centered card · scrolls internally · feels heavier</CaptionBubble>
    </FrameMockup>
  );
}

function PreviewFullPage() {
  return (
    <FrameMockup>
      <div className="absolute inset-0 bg-secondary/40 p-8 overflow-hidden">
        <div className="rounded-2xl border border-border bg-white p-6">
          <div className="flex items-center gap-2 mb-3">
            <a href="#" className="text-[11.5px] text-foreground/55 hover:text-foreground inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3"/> Back</a>
          </div>
          <DrawerProfileContent/>
        </div>
      </div>
      <CaptionBubble>Today's behaviour · separate page · only escape is browser back</CaptionBubble>
    </FrameMockup>
  );
}

/* ═══ SECTION 2 — TOP BAR LINK ═══════════════════════════════════════ */
function TopBarOptions() {
  const [pick, setPick] = useState("inline");
  return (
    <div>
      <Header
        eyebrow="Question 2"
        title="How should buyers see the profile link from the top bar?"
        sub="Today only the dropdown rows have a Profile link. Three ideas for surfacing it next to the seller's name without bloating the chrome."
      />

      <PickerTabs value={pick} onChange={setPick}
        items={[
          { k: "inline", label: "A · Inline ⓘ icon", rec: "Recommended" },
          { k: "split",  label: "B · Split pill (name = profile, ▾ = switcher)" },
          { k: "strip",  label: "C · Storefront strip below nav" },
        ]}/>

      <div className="mt-8">
        {pick === "inline" && <PreviewInline/>}
        {pick === "split"  && <PreviewSplit/>}
        {pick === "strip"  && <PreviewStrip/>}
      </div>

      <ProsCons
        title={pick === "inline" ? "Inline ⓘ icon · pros & cons"
             : pick === "split"  ? "Split pill · pros & cons"
             :                     "Storefront strip · pros & cons"}
        rows={
          pick === "inline" ? [
            ["Pro", "Smallest visual footprint — one tiny icon."],
            ["Pro", "Zero risk of mis-clicks: dropdown still works exactly as today."],
            ["Pro", "Standard ⓘ-as-info pattern — buyers expect it to do something."],
            ["Con", "Discoverability is medium — first-time users might not notice."],
          ] : pick === "split" ? [
            ["Pro", "Highest discoverability — the seller's name itself is the profile link."],
            ["Con", "Two click targets in one pill = harder UX. Mobile thumbs misfire."],
            ["Con", "Changes the muscle memory of every existing buyer. Risky change to chrome that ships every day."],
          ] : [
            ["Pro", "Most prominent — every page in storefront-mode shows it."],
            ["Pro", "Room to add 'View profile · MSA · Credit' triplet."],
            ["Con", "Adds a permanent strip = vertical real-estate cost on every page."],
            ["Con", "Visually competes with the existing storefront branding."],
          ]
        }/>
    </div>
  );
}

function PreviewInline() {
  return (
    <FrameMockup small>
      <div className="absolute top-0 inset-x-0 h-14 bg-white border-b border-border flex items-center px-5 gap-3">
        <div className="text-[12px] text-foreground/50 mono uppercase tracking-[0.16em]">Buyer hub</div>
        <div className="ml-auto inline-flex items-center gap-2 px-3 h-9 rounded-full border border-foreground/15 bg-white text-[13px]" style={{ borderColor: "#e8ff3a80" }}>
          <span className="w-5 h-5 rounded grid place-items-center text-[10px] font-bold display bg-[#0a1f1c] text-[#e8ff3a]">G</span>
          <span>GlowCo Beauty</span>
          <Annotated>
            <button className="text-foreground/45 hover:text-foreground" title="View profile"><Info className="w-3.5 h-3.5"/></button>
          </Annotated>
          <ChevronDown className="w-3.5 h-3.5 text-foreground/40"/>
        </div>
      </div>
      <CaptionBubble>The ⓘ opens the profile drawer — separate from ▾ which still toggles the supplier switcher.</CaptionBubble>
    </FrameMockup>
  );
}

function PreviewSplit() {
  return (
    <FrameMockup small>
      <div className="absolute top-0 inset-x-0 h-14 bg-white border-b border-border flex items-center px-5 gap-3">
        <div className="text-[12px] text-foreground/50 mono uppercase tracking-[0.16em]">Buyer hub</div>
        <div className="ml-auto inline-flex items-stretch rounded-full border border-foreground/15 bg-white text-[13px] overflow-hidden" style={{ borderColor: "#e8ff3a80" }}>
          <Annotated>
            <button className="px-3 h-9 inline-flex items-center gap-2 hover:bg-secondary/50">
              <span className="w-5 h-5 rounded grid place-items-center text-[10px] font-bold display bg-[#0a1f1c] text-[#e8ff3a]">G</span>
              <span>GlowCo Beauty</span>
            </button>
          </Annotated>
          <div className="w-px bg-foreground/10"/>
          <button className="px-2 inline-flex items-center hover:bg-secondary/50"><ChevronDown className="w-3.5 h-3.5 text-foreground/55"/></button>
        </div>
      </div>
      <CaptionBubble>Click the name → profile drawer. Click ▾ → supplier switcher dropdown.</CaptionBubble>
    </FrameMockup>
  );
}

function PreviewStrip() {
  return (
    <FrameMockup small>
      <div className="absolute top-0 inset-x-0 h-14 bg-white border-b border-border flex items-center px-5 gap-3">
        <div className="text-[12px] text-foreground/50 mono uppercase tracking-[0.16em]">Buyer hub</div>
        <div className="ml-auto inline-flex items-center gap-2 px-3 h-9 rounded-full border border-foreground/15 bg-white text-[13px]" style={{ borderColor: "#e8ff3a80" }}>
          <span className="w-5 h-5 rounded grid place-items-center text-[10px] font-bold display bg-[#0a1f1c] text-[#e8ff3a]">G</span>
          <span>GlowCo Beauty</span>
          <ChevronDown className="w-3.5 h-3.5 text-foreground/40"/>
        </div>
      </div>
      <Annotated>
        <div className="absolute top-14 inset-x-0 h-9 bg-secondary/60 border-b border-border flex items-center px-5 gap-4 text-[12px]">
          <div className="inline-flex items-center gap-2 text-foreground/65">
            <Store className="w-3.5 h-3.5"/>
            Shopping <strong className="text-foreground">GlowCo Beauty</strong>
          </div>
          <a href="#" className="underline underline-offset-2 hover:text-foreground">View profile</a>
          <span className="text-foreground/35">·</span>
          <span className="text-foreground/65">$25k Net-30 credit</span>
          <span className="text-foreground/35">·</span>
          <a href="#" className="underline underline-offset-2 hover:text-foreground">Open MSA</a>
        </div>
      </Annotated>
      <CaptionBubble>A thin permanent strip when in storefront-mode. Goes away in Hub mode.</CaptionBubble>
    </FrameMockup>
  );
}

/* ═══ SECTION 3 — CLOSE BEHAVIOUR ═════════════════════════════════════ */
function CloseOptions() {
  const [pick, setPick] = useState("drawer-close");
  return (
    <div>
      <Header
        eyebrow="Question 3"
        title="How does the buyer get back to where they were?"
        sub="Tightly coupled to Question 1 — when you pick the popup style, the close behaviour comes with it."
      />
      <PickerTabs value={pick} onChange={setPick}
        items={[
          { k: "drawer-close", label: "If you pick A (drawer)" },
          { k: "page-close",   label: "If you pick C (full page)" },
        ]}/>
      <div className="mt-8">
        {pick === "drawer-close" && <DrawerCloseModes/>}
        {pick === "page-close"   && <PageCloseModes/>}
      </div>
    </div>
  );
}

function DrawerCloseModes() {
  return (
    <div className="grid sm:grid-cols-3 gap-4">
      <CloseCard title="Click outside (backdrop)" Icon={X} desc="Tap the dim area behind the drawer. Most common pattern. Free — comes with the Sheet primitive."/>
      <CloseCard title="Press Esc" Icon={X} desc="Keyboard accessibility. Standard for power users. Free."/>
      <CloseCard title="Click the X button" Icon={X} desc="Top-right corner. Works on touch + click. Always-visible."/>
      <div className="sm:col-span-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-[13px] text-emerald-900 leading-relaxed">
        <strong>Recommendation:</strong> include all three (it's how every modern Sheet primitive ships by default). Buyer-page state (scroll position, expanded rows, in-progress PO) stays untouched throughout.
      </div>
    </div>
  );
}

function PageCloseModes() {
  return (
    <div className="grid sm:grid-cols-3 gap-4">
      <CloseCard title="Browser back button" Icon={ArrowLeft} desc="The native answer — but the buyer has to remember they came from the previous page. Today's behaviour."/>
      <CloseCard title="In-page 'Back' link" Icon={ArrowLeft} desc="Add an explicit 'Back to where I was' link at the top of /s/:handle, using document.referrer to label it intelligently. Works but referrer is unreliable across navigations."/>
      <CloseCard title="Sticky 'Back to your hub' bar" Icon={ArrowLeft} desc="A thin horizontal banner pinned to the top of /s/:handle when the visitor came from inside the buyer portal. Click → returns to last known buyer URL."/>
      <div className="sm:col-span-3 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-[13px] text-amber-900 leading-relaxed">
        <strong>Caveat:</strong> any solution here is duct-tape compared to the drawer pattern. The page itself can't preserve buyer state — only chrome around it can hint at "go back". Strongly recommend Section 1 → Option A.
      </div>
    </div>
  );
}

/* ═══ atoms ═════════════════════════════════════════════════════════ */
function Header({ eyebrow, title, sub }) {
  return (
    <div>
      <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/50">{eyebrow}</div>
      <h1 className="display text-3xl tracking-tight mt-1.5">{title}</h1>
      <p className="text-[13.5px] text-foreground/65 mt-2 max-w-2xl leading-relaxed">{sub}</p>
    </div>
  );
}
function PickerTabs({ value, onChange, items }) {
  return (
    <div className="mt-6 inline-flex items-center gap-1 rounded-full border border-border bg-white p-1">
      {items.map(it => (
        <button key={it.k}
          onClick={() => onChange(it.k)}
          className={`relative rounded-full px-4 h-9 text-[12px] mono uppercase tracking-[0.12em] transition-colors ${value === it.k ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}>
          {it.label}
          {it.rec && (
            <span className="absolute -top-1.5 -right-1.5 chip rounded-full bg-accent text-foreground border border-foreground/20 px-1.5 h-4 inline-flex items-center text-[9px] mono uppercase tracking-[0.1em] shadow-sm">{it.rec}</span>
          )}
        </button>
      ))}
    </div>
  );
}
function ProsCons({ title, rows }) {
  return (
    <div className="mt-8">
      <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mb-2">{title}</div>
      <div className="rounded-2xl border border-border bg-white divide-y divide-border">
        {rows.map(([tag, text], i) => (
          <div key={i} className="flex items-start gap-3 px-4 py-3">
            <span className={`chip mono text-[10px] uppercase tracking-[0.12em] rounded-full px-2 h-5 inline-flex items-center shrink-0 ${tag === "Pro" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`}>{tag}</span>
            <div className="text-[13px] text-foreground/80 leading-relaxed">{text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
function FrameMockup({ children, small }) {
  return (
    <div className={`relative rounded-3xl border border-border bg-secondary/60 overflow-hidden ${small ? "h-[260px]" : "h-[560px]"}`}>
      {children}
    </div>
  );
}
function BuyerPageBackdrop({ dimmed }) {
  return (
    <div className={`absolute inset-0 p-6 ${dimmed ? "bg-black/30" : ""}`}>
      <div className={`grid grid-cols-[180px_1fr] gap-4 h-full ${dimmed ? "opacity-30" : ""}`}>
        <div className="rounded-2xl border border-border bg-white p-3 space-y-2">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-7 rounded bg-secondary"/>)}
        </div>
        <div className="space-y-3">
          <div className="h-12 rounded-xl bg-white border border-border"/>
          <div className="grid grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-32 rounded-xl bg-white border border-border"/>)}
          </div>
        </div>
      </div>
    </div>
  );
}
function DrawerProfileContent() {
  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl grid place-items-center display text-xl bg-[#0a1f1c] text-[#e8ff3a]">G</div>
        <div className="flex-1">
          <div className="display text-xl">GlowCo Beauty</div>
          <div className="text-[12.5px] text-foreground/65">Clean, cruelty-free skincare. Made in California.</div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className="chip rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 h-6 inline-flex items-center text-[11px] mono uppercase tracking-[0.1em]"><ShieldCheck className="w-3 h-3 mr-1"/> Verified</span>
        <span className="chip rounded-full bg-secondary border border-border px-2 h-6 inline-flex items-center text-[11px] text-foreground/65"><MapPin className="w-3 h-3 mr-1"/>Los Angeles, CA</span>
      </div>
      <div className="mt-5">
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Policies</div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {[
            ["Inspection window", "7 days"],
            ["Lead time",         "3 days"],
            ["Payment terms",     "Net-30"],
            ["Returns",           "Per MSA"],
          ].map(([l, v]) => (
            <div key={l} className="rounded-lg border border-border bg-secondary/40 p-3">
              <div className="text-[10.5px] mono uppercase tracking-[0.12em] text-foreground/55">{l}</div>
              <div className="text-[14px] mt-0.5">{v}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-5">
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Trust signals</div>
        <div className="mt-2 space-y-1.5">
          {[
            "EIN verified · Middesk",
            "Bank account verified · Plaid",
            "Insurance on file · Allianz Trade",
          ].map(c => (
            <div key={c} className="flex items-center gap-2 text-[12.5px] text-foreground/75">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/>{c}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
function CaptionBubble({ children }) {
  return (
    <div className="absolute bottom-3 left-3 right-3 rounded-xl bg-foreground text-background px-3 py-2 text-[11.5px] leading-snug max-w-fit mx-auto">
      <Sparkles className="w-3 h-3 inline -mt-0.5 mr-1 text-accent"/>
      {children}
    </div>
  );
}
function CloseCard({ title, Icon, desc }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-secondary grid place-items-center"><Icon className="w-4 h-4 text-foreground/65"/></div>
        <div className="display text-base">{title}</div>
      </div>
      <p className="text-[12.5px] text-foreground/65 leading-relaxed mt-2.5">{desc}</p>
    </div>
  );
}
function Annotated({ children }) {
  return (
    <span className="relative inline-flex">
      <span className="absolute -inset-1.5 rounded-full ring-2 ring-accent/80 ring-offset-1 ring-offset-white pointer-events-none" aria-hidden/>
      {children}
    </span>
  );
}
